import './App.css';
import { lazy, Suspense } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes } from "react-router-dom";
import ProtectedRoute from './components/protected-route';
import { init } from '@amplitude/analytics-browser';

import ZooView from "./dev/zoo/Zoo";
import PagesView from "./dev/pages/Pages";
import BasicInvoiceView from "./dev/pages/basic-invoice/BasicInvoice";
import Checkout from './dev/pages/checkout/Checkout';
import Receipt from './dev/pages/receipt/Receipt';
import InvoiceViewController from './client/InvoiceViewController';
import InvoiceView from './client/InvoiceView';
import InvoiceLoadingView from './client/InvoiceLoadingView';
import InvoiceReceiptView from "./client/InvoiceReceiptView";
import InvoiceSelectionView from "./client/InvoiceSelectionView";
import InvoicePaymentView from "./client/InvoicePaymentView";
import PracticeAuthenticator from './practice/practice-authenticator/PracticeAuthenticator';
import SubscriptionCollectionsLoadingView from './client/collections/SubscriptionCollectionsLoadingView';
import SubscriptionCollectionsPaymentView from './client/collections/SubscriptionCollectionsPaymentView';
import SubscriptionCollectionsPendingChargeView from './client/collections/SubscriptionCollectionsPendingChargeView';
import SubscriptionCollectionsPaymentOkView from './client/collections/SubscriptionCollectionsPaymentOkView';
import PaymentRequestEntryView from './client/payment-request/PaymentRequestEntryView';
import PaymentRequestLoadingView from './client/payment-request/PaymentRequestLoadingView';
import PaymentRequestView from './client/payment-request/PaymentRequestView';
import PaymentRequestCheckoutView from './client/payment-request/PaymentRequestCheckoutView';
import PaymentRequestFinishView from './client/payment-request/PaymentRequestFinishView';
import PaymentRequestReceiptView from './client/payment-request/PaymentRequestReceiptView';
import ServiceAgreement from './client/contracts/ServiceAgreement';
import PrivacyPolicy from './client/contracts/PrivacyPolicy';
import SnoutLoadingDots from './components/snout-loading-dots/SnoutLoadingDots';
import WizardFlow from './client/SelfRegistration/ClientSelfRegWizard';
import PlanReceipt from './client/PlanReceipt/PlanReceipt';

const CreatePasswordResetLink = lazy(() => import('./pages/europa/CreatePasswordResetLink'));
const ResetPassword = lazy(() => import('./pages/europa/ResetPassword'));
const Login = lazy(() => import('./pages/europa/Login'));
const VetListing = lazy(() => import('./pages/europa/VetListing'));
const VetCreate = lazy(() => import('./pages/europa/VetCreate'));
const VetUpdate = lazy(() => import('./pages/europa/VetUpdate'));
const VetDetails = lazy(() => import('./pages/europa/VetDetails'));
const PlanListing = lazy(() => import('./pages/europa/PlanListing'));
const PlanCreate = lazy(() => import('./pages/europa/PlanCreate'));
const CorePlanCreate = lazy(() => import('./pages/europa/CorePlanCreate'));
const AddonPlanCreate = lazy(() => import('./pages/europa/AddonPlanCreate'));
const PlanDetails = lazy(() => import('./pages/europa/PlanDetails'));
const CorePlanDetails = lazy(() => import('./pages/europa/CorePlanDetails'));
const AddonPlanDetails = lazy(() => import('./pages/europa/AddonPlanDetails'));
const PlanCompatibility = lazy(() => import('./pages/europa/PlanCompatibility'));
const PlanInstanceListing = lazy(() => import('./pages/europa/PlanInstanceListing'));
const PlanInstanceDetails = lazy(() => import('./pages/europa/PlanInstanceDetails'));
const PlanInstanceRenew = lazy(() => import('./pages/europa/PlanInstanceRenew'));
const InvoiceListing = lazy(() => import('./pages/europa/InvoiceListing'));
const VetInvoiceListing = lazy(() => import('./pages/europa/VetInvoiceListing'));
const PaymentRequestListing = lazy(() => import('./pages/europa/PaymentRequestListing'));
const InvoiceDetails = lazy(() => import('./pages/europa/InvoiceDetails'));
const Clients = lazy(() => import('./pages/europa/TestData/Clients'));
const Patients = lazy(() => import('./pages/europa/TestData/Patients'));
const InvoiceCreate = lazy(() => import('./pages/europa/TestData/Invoice'));
const PatientDetails = lazy(() => import('./pages/europa/PatientDetails'));
const AlertConfig = lazy(() => import('./pages/europa/AlertConfig'));
const PromoListing = lazy(() => import('./pages/europa/PromoListing'));
const PromoDetails = lazy(() => import('./pages/europa/PromoDetails'));
const PromoCreate = lazy(() => import('./pages/europa/PromoCreate'));
const SubscriptionCollectionsViewController = lazy(() => import('./client/collections/SubscriptionCollectionsViewController'));
const PaymentRequestViewController = lazy(() => import('./client/payment-request/PaymentRequestViewController'));

function App() {
  // initialize amplitude
  if (process.env.NODE_ENV === 'production' && window?.location?.host === 'wellness.snoutid.com') init('e42a18cbef7615867290c3c1aaffd2fa');

  // navigate to europa, /practice/demo/wellness is dead
  if (window.location.protocol === 'http:' && window.location.host === 'localhost:3000' && window.location.pathname === '/') {
    window.location = 'http://localhost:3000/europa/';
  }

  return (
    <Router>
      <Suspense fallback={<div style={{ display: 'flex', flexDirection: 'column', flex: '1', justifyContent: 'center', alignItems: 'center' }}><SnoutLoadingDots /></div>}>
          <Routes>
            {/* ADMIN Routes */}
            <Route exact path="/europa" element={<Navigate to="/europa/login" replace />} />
            <Route exact path="/europa/login" element={<Login />} />   {/* This is the redirect target for ProtectedRoute */}
            <Route exact path="/europa/reset" element={<CreatePasswordResetLink />} />
            <Route exact path="/europa/password/:resetToken" element={<ResetPassword />} />
            <Route exact path='/europa' element={<ProtectedRoute />}>
              <Route exact path="invoices" element={<InvoiceListing />} />
              <Route exact path="vet/:vet_id/invoices" element={<VetInvoiceListing />} />
              <Route exact path="vet/:vet_id/invoice/create" element={<InvoiceCreate />} />
              <Route exact path="vet/:vet_id/payment_requests" element={<PaymentRequestListing />} />
              <Route exact path="payment_requests" element={<PaymentRequestListing />} />
              <Route exact path="vets" element={<VetListing />} />
              <Route exact path="vet/create" element={<VetCreate />} />
              <Route exact path="vet/:id" element={<VetDetails />} />
              <Route exact path="vet/:id/update" element={<VetUpdate />} />
              <Route exact path="vet/:vet_id/plans" element={<PlanListing />} />
              <Route exact path="vet/:vet_id/plan/create" element={<PlanCreate />} />
              <Route exact path="vet/:vet_id/core_plan/create" element={<CorePlanCreate />} />
              <Route exact path="vet/:vet_id/addon/create" element={<AddonPlanCreate />} />
              <Route exact path="vet/:vet_id/plan/:plan_id" element={<PlanDetails />} />
              <Route exact path="vet/:vet_id/core_plan/:plan_id" element={<CorePlanDetails />} />
              <Route exact path="vet/:vet_id/addon/:plan_id" element={<AddonPlanDetails />} />
              <Route exact path="vet/:vet_id/plan/:plan_id/compatibility" element={<PlanCompatibility />} />
              <Route exact path="plan/:plan_id" element={<PlanDetails />} />
              <Route exact path="invoice/:id" element={<InvoiceDetails />} />
              <Route exact path="clients" element={<Clients />} />
              <Route exact path="patients" element={<Patients />} />
              <Route exact path="patient/:id" element={<PatientDetails />} />
              <Route exact path="vet/:vet_id/plan_instances" element={<PlanInstanceListing />} />
              <Route exact path="plan_instance/:id" element={<PlanInstanceDetails />} />
              <Route exact path="vet/:vet_id/plan_instance/:id/renew" element={<PlanInstanceRenew />} />
              <Route exact path="vet/:vet_id/promos" element={<PromoListing />} />
              <Route exact path="vet/:vet_id/promo/create" element={<PromoCreate />} />
              <Route exact path="promo/:id" element={<PromoDetails />} />
              <Route exact path="alert_config" element={<AlertConfig />} />
            </Route>

            {/* DEV Routes*/}
            {/*<Route exact path="/" element={<DevMenuView />} />*/}
            <Route exact path="/component-zoo" element={<ZooView />} />
            <Route exact path="/prototype-pages" element={<PagesView />} />
            <Route exact path="/prototype-pages/basic-invoice" element={<BasicInvoiceView />} />
            <Route exact path="/prototype-pages/checkout" element={<Checkout />} />
            <Route exact path="/prototype-pages/receipt" element={<Receipt />} />

            {/* CLIENT Routes */}
            <Route exact path="/join" element={<Navigate to="/subscription" replace />} />
            <Route path="request" element={<PaymentRequestViewController />}>
              <Route path="" element={<PaymentRequestEntryView />} />
              <Route path="load/:code" element={<PaymentRequestLoadingView />} />
              <Route path=":code" element={<PaymentRequestView />}>
                <Route path="checkout" element={<PaymentRequestCheckoutView />} />
                <Route path="finish" element={<PaymentRequestFinishView />} />
                <Route path="receipt" element={<PaymentRequestReceiptView />} />
              </Route>
            </Route>
            <Route path="invoice/:invoiceId" element={<InvoiceViewController />} >
              <Route path="" element={<InvoiceLoadingView />} />
              <Route path="view" element={<InvoiceView />}>
                <Route path="selection" element={<InvoiceSelectionView />} />
                <Route path="payment" element={<InvoicePaymentView />} />
              </Route>
              <Route path="receipt" element={<InvoiceReceiptView />} />
            </Route>
            <Route path="payment/plan/:planId" element={<SubscriptionCollectionsViewController />} >
              <Route path="" element={<SubscriptionCollectionsLoadingView />} />
              <Route path="updatePayment" element={<SubscriptionCollectionsPaymentView />} />
              <Route path="receipt" element={<SubscriptionCollectionsPaymentOkView />} />
              <Route path="pending" element={<SubscriptionCollectionsPendingChargeView />} />
            </Route>

            {/* PRACTICE Routes */}
            <Route path="practice">
              <Route path=":token" element={<PracticeAuthenticator />}>
              </Route>
            </Route>

            { /* CONTRACT Routes */}
            <Route path='contracts'>
              <Route path=":token" element={<PracticeAuthenticator />}>
                <Route path="serviceagreement" element={<ServiceAgreement />}></Route>
              </Route>
            </Route>

            { /* PUBLIC ROUTES */ }
            <Route path='membership-agreement' element={<ServiceAgreement />}></Route>
            <Route path='privacy' element={<PrivacyPolicy />}></Route>
            <Route path='enroll/:slug' element={<WizardFlow/>}></Route>
            <Route path='receipt/careplan/:guid' element={<PlanReceipt/>}></Route>
          </Routes>
      </Suspense>
    </Router>
  );
}

export default App;
